import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import logoSinarmasWhite from 'images/logo-sinarmas-white.svg';
import logoWhite from 'images/logo-white.svg';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRoleInfo } from 'hooks/useRoleInfo';

interface NavItem {
  label: string;
  to?: string;
  href?: string;
}

const PageHeader: React.FC = () => {
  const { isAuthenticated, profile, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { userIsPillarPIC, userIsCategoryJudge, userIsSuperAdmin, userPillar } =
    useRoleInfo();
  const { pathname, hash } = useLocation();

  const getRoleLabel = () => {
    if (userIsSuperAdmin) return 'Award Admin';
    if (userIsPillarPIC)
      return `Pillar PIC${userPillar ? ` - ${userPillar}` : ''}`;
    if (userIsCategoryJudge) return 'Category Judge';
    return '';
  };

  const navItems: NavItem[] = [{ label: 'Home', to: '/' }];

  if (!isAuthenticated()) {
    navItems.push(
      { label: 'About', href: '#about' },
      { label: 'Categories', href: '#categories' },
      { label: '2026 Edition', href: '#timeline' }
    );
  }

  if (isAuthenticated()) {
    if (userIsPillarPIC) {
      navItems.push({
        label: 'Pre-Selection Ranking',
        to: '/smdd-submissions',
      });
      navItems.push({ label: 'Qualification Ranking', to: '/submissions' });
    } else if (userIsCategoryJudge) {
      navItems.push({ label: 'Qualification Evaluation', to: '/evaluation' });
      navItems.push({ label: 'Finalist', to: '/finalists' });
      navItems.push({ label: 'Final Evaluation', to: '/final-evaluation' });
    } else if (userIsSuperAdmin) {
      navItems.push({ label: 'Categories', to: '/category-mapping' });
      navItems.push({
        label: 'Pre-Selection Ranking',
        to: '/smdd-submissions',
      });
      navItems.push({ label: 'Qualification Ranking', to: '/submissions' });
      navItems.push({ label: 'Final Ranking', to: '/final-submissions' });
    }
  }

  const visibleItems = navItems;

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !target.closest('#mobile-menu-button')
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderNavLink = (item: NavItem, isMobile = false) => {
    const isActive =
      item.to === '/'
        ? pathname === '/' && hash === ''
        : item.to
          ? pathname === item.to
          : item.href
            ? pathname === '/' && hash === item.href.replace('/', '')
            : false;

    const className = isMobile
      ? `${
          isActive ? 'text-red-300 font-bold' : 'text-white'
        } hover:text-red-400 transition-colors py-2 border-b border-gray-900 w-full font-baijamjuree`
      : `font-baijamjuree ${
          isActive ? 'text-red-300 font-bold' : 'text-white'
        } hover:text-red-300 transition-colors relative py-1 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-red-300 after:transition-transform after:duration-300 ${
          isActive
            ? 'after:scale-x-100'
            : 'after:scale-x-0 hover:after:scale-x-100'
        }`;

    const onClick = isMobile ? () => setIsMenuOpen(false) : undefined;

    if (item.href) {
      return (
        <a
          key={item.label}
          href={item.href}
          className={className}
          onClick={onClick}
        >
          {item.label}
        </a>
      );
    }

    if (item.to) {
      return (
        <Link
          key={item.label}
          to={item.to}
          className={className}
          onClick={onClick}
        >
          {item.label}
        </Link>
      );
    }

    return null;
  };

  return (
    <header className="sticky top-0 z-50 bg-black">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center space-x-4">
          <img
            src={logoSinarmasWhite}
            alt="Sinarmas Logo"
            className="h-8 w-auto"
          />
          <img src={logoWhite} alt="SMDD Logo" className="h-8 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8 items-center">
          {visibleItems.map(item => renderNavLink(item))}
        </nav>

        {/* Desktop User/Login */}
        <div className="hidden md:flex items-center">
          {isAuthenticated() ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-red-600 text-white font-baijamjuree font-bold focus:outline-none cursor-pointer hover:bg-red-700 transition-colors uppercase"
                title={profile?.fullName || ''}
              >
                {profile?.fullName ? profile.fullName.charAt(0) : '?'}
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20">
                  <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100 mb-1 font-semibold uppercase truncate font-baijamjuree">
                    {profile?.fullName}
                  </div>
                  <div className="px-4 py-2 text-xs text-gray-400 border-b border-gray-100 mb-1 font-semibold uppercase truncate font-baijamjuree">
                    ({getRoleLabel()})
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 font-baijamjuree"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="border border-white text-white rounded-full px-5 py-2 font-baijamjuree font-semibold hover:bg-white hover:text-black transition-colors"
            >
              Login
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          id="mobile-menu-button"
          onClick={toggleMenu}
          className="md:hidden text-white p-2 focus:outline-none"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <XMarkIcon className="h-7 w-7" />
          ) : (
            <Bars3Icon className="h-7 w-7" />
          )}
        </button>
      </div>

      {/* Mobile Menu Panel */}
      {isMenuOpen && (
        <div
          className="md:hidden bg-black border-t border-gray-800 max-h-[calc(100vh-5rem)] overflow-y-auto"
          ref={menuRef}
        >
          <nav className="flex flex-col space-y-4 px-6 py-6 font-baijamjuree">
            {visibleItems.map(item => renderNavLink(item, true))}

            {isAuthenticated() ? (
              <div className="pt-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-red-600 text-white font-baijamjuree font-bold uppercase"
                    title={profile?.fullName || ''}
                  >
                    {profile?.fullName ? profile.fullName.charAt(0) : '?'}
                  </span>
                  <span className="text-gray-400 text-sm font-baijamjuree">
                    {profile?.fullName}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-sm border border-red-500 text-red-500 px-3 py-1 rounded-full hover:bg-red-500 hover:text-white transition-colors font-baijamjuree"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="mt-4 bg-white text-black text-center py-3 rounded-md font-bold hover:bg-gray-200 transition-colors font-baijamjuree"
                onClick={() => setIsMenuOpen(false)}
              >
                Login
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default PageHeader;
